import React, { useState, useEffect } from 'react';
import { IMAGES } from '../constants/images';

const slides = [
  {
    title: 'HIGH-SPEED\nINTERNET',
    subtitle: 'Reliable internet for your home and business.\nStream, work, and stay connected.',
    bg: 'from-primary via-primary/80 to-transparent',
    accent: '#f59e0b',
    accentText: '#fde047',
    cardTitle: 'Internet that keeps up with you',
    cardText: 'Whether you work from home, stream, or browse, get a connection you can count on. We focus on reliability and support so you stay online.',
  },
  {
    title: 'CABLE TV\nSERVICES',
    subtitle: 'HD channels, sports, and on‑demand from licensed cable & TV providers.\nEntertainment the whole family can enjoy.',
    bg: 'from-primaryLight via-primaryLight/80 to-transparent',
    accent: '#10b981',
    accentText: '#7dd3fc',
    cardTitle: 'Entertainment for everyone',
    cardText: 'From news and sports to movies and shows, licensed providers bring the channels you want. No‑pressure guidance — pricing varies by provider and location.',
  },
];

const featureCards = [
  { icon: '⚡', label: 'Reliable', text: 'Stable connection' },
  { icon: '📡', label: 'Enrollment', text: 'Sales & sign-up help' },
  { icon: '🛡️', label: 'Simple', text: 'Clear plans' },
  { icon: '🎯', label: 'Flexible', text: 'Options for you' },
];

const Hero = () => {
  const [current, setCurrent] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const [imgError, setImgError] = useState({});

  const heroSrc = (index) => {
    if (imgError[index]) return IMAGES.hero[(index + 1) % IMAGES.hero.length];
    return IMAGES.hero[index];
  };

  const handleImgError = (index) => () => setImgError((prev) => ({ ...prev, [index]: true }));

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
      setAnimKey((k) => k + 1);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const handleSlide = (idx) => {
    setCurrent(idx);
    setAnimKey((k) => k + 1);
  };

  return (
    <section className="relative min-h-[520px] xs:min-h-[600px] sm:min-h-[680px] md:min-h-[760px] lg:min-h-[860px] xl:min-h-[920px] 2xl:min-h-[min(94vh,940px)] flex items-center overflow-hidden">

      {/* ── Full-bleed background image ── */}
      <div className="absolute inset-0">
        <img
          src={heroSrc(current)}
          alt=""
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
          style={{ objectPosition: 'center right' }}
          onError={handleImgError(current)}
        />
        {/* Gradient: strong on left (text), fades to transparent on right (image visible) */}
        <div
          className={`absolute inset-0 bg-gradient-to-r ${slides[current].bg}`}
          style={{ transition: 'background 0.8s ease' }}
        />
        {/* Extra dark vignette on left for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent" />
        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/40 to-transparent" />
      </div>

      {/* ── Decorative floating orbs ── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute w-[500px] h-[500px] rounded-full blur-3xl opacity-20 transition-colors duration-1000"
          style={{ backgroundColor: slides[current].accent, top: '-10%', left: '25%' }}
        />
        <div
          className="absolute w-[300px] h-[300px] rounded-full blur-2xl opacity-15 transition-colors duration-1000"
          style={{ backgroundColor: slides[current].accent, bottom: '5%', right: '10%' }}
        />
      </div>

      {/* ── Main content: two-column grid ── */}
      <div className="mx-auto px-4 xs:px-5 sm:px-6 md:px-8 relative z-10 w-full" style={{ maxWidth: 'min(100%, var(--content-max))' }}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-16 items-center">

          {/* LEFT – Text */}
          <div>
            {/* Badge */}
            {/* Headline */}
            <h1
              key={`title-${animKey}`}
              className="text-4xl xs:text-5xl sm:text-6xl md:text-7xl lg:text-7xl xl:text-8xl 2xl:text-8xl font-black text-white leading-[1.05] mb-4 sm:mb-6 animate-slideInLeft whitespace-pre-line"
              style={{ textShadow: '0 4px 32px rgba(0,0,0,0.4)' }}
            >
              {slides[current].title}
            </h1>

            {/* Accent line */}
            <div
              className="h-1 w-20 rounded-full mb-6 transition-colors duration-700"
              style={{ backgroundColor: slides[current].accent }}
            />

            {/* Subtitle */}
            <p
              key={`sub-${animKey}`}
              className="text-white/90 text-base xs:text-lg sm:text-xl md:text-2xl font-medium leading-relaxed whitespace-pre-line animate-fadeInUp max-w-lg"
              style={{ animationDelay: '0.15s' }}
            >
              {slides[current].subtitle}
            </p>

            {/* CTA */}
            <div
              key={`cta-${animKey}`}
              className="mt-10 flex flex-wrap gap-4 animate-fadeInUp"
              style={{ animationDelay: '0.3s' }}
            >
              <a
                href="tel:+18888802585"
                className="bg-secondary hover:bg-secondaryDark text-white font-extrabold px-8 py-4 rounded-full transition-all duration-300 hover:-translate-y-1 shadow-xl hover:shadow-secondary/40 text-base"
              >
                Call Us +1 (888) 880-2585
              </a>
            </div>

            {/* Compliance: independent-retailer status, near the phone CTA */}
            <p className="mt-5 text-white/75 text-xs sm:text-sm max-w-md leading-relaxed">
              InternetTVService is an Authorized Dealer for Internet and TV services — not affiliated with or
              endorsed by any provider.
            </p>

            {/* Slide indicators */}
            <div className="mt-10 flex items-center gap-3">
              {slides.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSlide(idx)}
                  className={`rounded-full transition-all duration-400 ${idx === current
                    ? 'w-10 h-2.5 bg-white'
                    : 'w-2.5 h-2.5 bg-white/30 hover:bg-white/60'
                    }`}
                />
              ))}
              <span className="ml-2 text-white/50 text-xs font-mono">
                {String(current + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
              </span>
            </div>
          </div>

          {/* RIGHT – Feature cards grid (text-based content) */}
          <div
            key={`cards-${animKey}`}
            className="hidden lg:grid grid-cols-2 gap-4 animate-fadeInUp"
            style={{ animationDelay: '0.2s' }}
          >
            {/* Main card – text content per slide */}
            <div
              className="col-span-2 rounded-2xl p-6 backdrop-blur-md border border-white/20 flex flex-col gap-4 shadow-2xl"
              style={{ background: 'rgba(255,255,255,0.08)' }}
            >
              <div className="flex items-start gap-4">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 shadow-lg"
                  style={{ backgroundColor: slides[current].accent + '33', border: `1.5px solid ${slides[current].accent}66` }}
                >
                  {current === 0 ? '🌐' : '📺'}
                </div>
                <div>
                  <div
                    className="text-xl font-bold mb-1 transition-colors duration-700"
                    style={{
                      color: slides[current].accentText ?? slides[current].accent,
                      textShadow: '0 1px 2px rgba(0,0,0,0.4), 0 0 20px rgba(0,0,0,0.2)',
                    }}
                  >
                    {slides[current].cardTitle}
                  </div>
                  <p className="text-white/80 text-sm leading-relaxed">
                    {slides[current].cardText}
                  </p>
                </div>
              </div>
            </div>

            {/* 4 smaller feature cards – text only */}
            {featureCards.map((card, i) => (
              <div
                key={i}
                className="rounded-2xl p-5 backdrop-blur-md border border-white/15 shadow-xl hover:border-white/30 transition-all duration-300 hover:-translate-y-1 group cursor-default"
                style={{
                  background: 'rgba(255,255,255,0.07)',
                  animationDelay: `${0.25 + i * 0.07}s`,
                }}
              >
                <div className="text-2xl mb-3">{card.icon}</div>
                <div
                  className="text-lg font-bold mb-0.5 transition-colors duration-700"
                  style={{
                    color: slides[current].accentText ?? slides[current].accent,
                    textShadow: '0 1px 2px rgba(0,0,0,0.35), 0 0 12px rgba(0,0,0,0.15)',
                  }}
                >
                  {card.text}
                </div>
                <div className="text-white/60 text-xs font-semibold uppercase tracking-wider">
                  {card.label}
                </div>
              </div>
            ))}

          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
